<template>
  <div
    class="item-selector__content-item tw-flex tw-items-center"
    :class="computedClasses"
    :data-navigable="navigable"
    :tabindex="focused ? 0 : -1"
    @click="handleClick"
    @keydown.enter.prevent="handleEnter"
    @keydown.space.prevent="handleSpace"
  >
    <!-- Checkbox for multi-select -->
    <q-checkbox
      v-if="showCheckbox"
      :model-value="selected"
      size="xs"
      dense
      class="item-selector__checkbox tw-pointer-events-none"
      tabindex="-1"
      :disable="false"
    />

    <!-- Icon/Avatar slot -->
    <div v-if="$slots.icon" class="item-selector__icon">
      <slot name="icon" />
    </div>

    <!-- Main content -->
    <div class="item-selector__content tw-min-w-0 tw-flex-1">
      <div
        v-if="$slots.primary || primaryText"
        class="item-selector__content-primary"
      >
        <slot name="primary">{{ primaryText }}</slot>
      </div>
      <div
        v-if="$slots.secondary || secondaryText"
        class="item-selector__content-secondary"
      >
        <slot name="secondary">{{ secondaryText }}</slot>
      </div>
    </div>

    <!-- Badge/Status slot -->
    <div v-if="$slots.badge" class="item-selector__badge">
      <slot name="badge" />
    </div>

    <!-- Action slot -->
    <div v-if="$slots.action" class="item-selector__action">
      <slot name="action" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props<T = unknown> {
  /** Whether this item is selected */
  selected?: boolean;

  /** Whether this item is focused */
  focused?: boolean;

  /** Whether this item is disabled */
  disabled?: boolean;

  /** Whether this item is archived */
  archived?: boolean;

  /** Whether this is a hierarchical item (folder/category) */
  hierarchical?: boolean;

  /** Show checkbox for multi-select mode */
  showCheckbox?: boolean;

  /** Primary text to display */
  primaryText?: string;

  /** Secondary text to display */
  secondaryText?: string;

  /** Custom CSS classes */
  customClass?: string | string[];

  /** Whether this item is navigable via keyboard */
  navigable?: boolean;

  /** The data item this row represents */
  item?: T;
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  focused: false,
  disabled: false,
  archived: false,
  hierarchical: false,
  showCheckbox: false,
  navigable: true,
});

const emit = defineEmits<{
  click: [item?: unknown];
  toggle: [item?: unknown];
  select: [item?: unknown];
  'item-selected': [item?: unknown];
  'item-toggled': [item?: unknown];
}>();

const computedClasses = computed(() => {
  const classes: string[] = [];

  if (props.selected) classes.push('item-selector__content-item--selected');
  if (props.focused) classes.push('item-selector__content-item--focused');
  if (props.disabled) classes.push('item-selector__content-item--disabled');
  if (props.archived) classes.push('item-selector__content-item--archived');
  if (props.hierarchical)
    classes.push('item-selector__content-item--hierarchical');

  if (props.customClass) {
    if (Array.isArray(props.customClass)) {
      classes.push(...props.customClass);
    } else {
      classes.push(props.customClass);
    }
  }

  return classes;
});

const handleClick = () => {
  if (props.disabled) return;

  // Emit different events based on whether we're in multi-select mode
  if (props.showCheckbox) {
    // Multi-select mode - emit toggle event
    emit('item-toggled', props.item);
  } else {
    // Single-select mode - emit select event
    emit('item-selected', props.item);
  }

  // Also emit generic click for backwards compatibility
  emit('click', props.item);
};

const handleEnter = () => {
  handleClick();
};

const handleSpace = () => {
  // Space key always triggers the same as click
  handleClick();
};
</script>

<style scoped>
.item-selector__content-item {
  @apply tw-p-3;
  @apply tw-cursor-pointer;
  @apply tw-transition-colors tw-duration-150;
  min-height: 36px;
  user-select: none;
  position: relative;
  z-index: 0;

  &:hover:not(.item-selector__content-item--selected) {
    @apply tw-bg-black tw-bg-opacity-5;
  }

  &:focus {
    @apply tw-outline-none;
    box-shadow: inset 0 0 0 1px var(--primary-color);
    z-index: 1;
  }

  &.item-selector__content-item--selected {
    background-color: rgba(var(--primary-color-rgb), 0.2);
  }

  &.item-selector__content-item--selected:hover {
    background-color: rgba(var(--primary-color-rgb), 0.25);
  }

  &.item-selector__content-item--selected:focus {
    background-color: rgba(var(--primary-color-rgb), 0.25);
    box-shadow: inset 0 0 0 1px var(--primary-color);
  }

  &.item-selector__content-item--focused {
    box-shadow: inset 0 0 0 1px var(--primary-color);
    z-index: 1;
  }

  &.item-selector__content-item--disabled {
    @apply tw-opacity-50 tw-cursor-not-allowed;
    pointer-events: none;
  }

  &.item-selector__content-item--archived {
    @apply tw-opacity-60;
  }

  &.item-selector__content-item--hierarchical {
    @apply tw-font-medium;
  }
}

.item-selector__checkbox {
  @apply tw-mr-2 tw-shrink-0;
}

.item-selector__icon {
  @apply tw-mr-2 tw-shrink-0 tw-flex tw-items-center;
}

.item-selector__content {
  @apply tw-flex tw-flex-col;
}

.item-selector__content-primary {
  @apply tw-text-sm tw-font-medium;
}

.item-selector__content-secondary {
  @apply tw-text-xs tw-text-gray-medium;
  margin-top: 2px;
}

.item-selector__badge {
  @apply tw-ml-auto tw-flex tw-items-center tw-gap-3;
}

.item-selector__action {
  @apply tw-ml-3 tw-shrink-0;
}
</style>
